import React, { useEffect, useRef, useState } from 'react';

// Pro scopes drawn from the GradedCanvas readback (props.stats):
//   histogram  — RGB + luma distribution, with axes / gridlines / tonal zones
//   waveform   — per-column luma intensity (exposure across the frame)
//   parade     — per-column R/G/B side-by-side (white balance / channel balance)
//   vectorscope— Cb/Cr chroma plane (hue + saturation, skin-tone line)
// stats = { r,g,b,l,bins, wfR,wfG,wfB,wfL, vec, cols, rows, vec_n, samples }.
const MODES = [
  { id: 'hist', label: 'Histogram' },
  { id: 'wave', label: 'Waveform' },
  { id: 'parade', label: 'Parade' },
  { id: 'vector', label: 'Vector' },
];

// 18% photographic mid-grey lands at ~46% code value through the sRGB/Rec.709
// transfer (0.18 linear → ~0.461 encoded). Handy creative-exposure reference.
const MID_GREY = 0.461;

export default function Scopes({ stats, mode: forcedMode }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('hist');
  const active = forcedMode || mode;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Render at device resolution so the axis text + 1px gridlines stay crisp
    // instead of being stretched up from a small backing store by the CSS width.
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cssW = Math.max(180, Math.round(canvas.clientWidth || 300));
    const cssH = active === 'vector' ? cssW : active === 'hist' ? 172 : 140;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.height = `${cssH}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#0b0e13';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (!stats) return;

    if (active === 'hist') {
      // Draw in CSS pixels (transform handles the DPR scale).
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const W = cssW;
      const H = cssH;
      const { r, g, b, l, bins } = stats;
      const ml = 26;
      const mr = 10;
      const mt = 14;
      const mb = 18;
      const px = ml;
      const py = mt;
      const pw = W - ml - mr;
      const ph = H - mt - mb;
      const fx = (f) => px + f * pw;
      const fy = (f) => py + ph - f * ph;

      // Plot background.
      ctx.fillStyle = '#0d1117';
      ctx.fillRect(px, py, pw, ph);

      // Clipping zones: the bottom ~2% (crushed blacks) and top ~2% (blown
      // whites) where detail is lost — tinted red as a warning band.
      const clipW = Math.max(2, pw * 0.02);
      ctx.fillStyle = 'rgba(255,64,64,0.12)';
      ctx.fillRect(px, py, clipW, ph);
      ctx.fillRect(px + pw - clipW, py, clipW, ph);

      ctx.font = '9px ui-sans-serif, system-ui, -apple-system, sans-serif';

      // Horizontal gridlines + Y axis (frequency as % of the peak bin).
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.fillStyle = 'rgba(165,175,190,0.7)';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 1;
      [0, 0.25, 0.5, 0.75, 1].forEach((f) => {
        const y = fy(f);
        ctx.beginPath();
        ctx.moveTo(px, y);
        ctx.lineTo(px + pw, y);
        ctx.stroke();
        ctx.fillText(f === 0 ? '0' : `${Math.round(f * 100)}`, px - 4, y);
      });

      // Vertical gridlines + X axis (tonal value, 0 = black … 100 = white).
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      [0, 0.25, 0.5, 0.75, 1].forEach((f) => {
        const x = fx(f);
        ctx.strokeStyle = 'rgba(255,255,255,0.06)';
        ctx.beginPath();
        ctx.moveTo(x, py);
        ctx.lineTo(x, py + ph);
        ctx.stroke();
        ctx.fillStyle = 'rgba(165,175,190,0.7)';
        ctx.fillText(`${Math.round(f * 100)}`, x, py + ph + 4);
      });

      // 18% mid-grey reference (creative exposure anchor).
      const mgx = fx(MID_GREY);
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.beginPath();
      ctx.moveTo(mgx, py);
      ctx.lineTo(mgx, py + ph);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText('18%', mgx, py + ph - 1);

      // Tonal-zone labels across the top of the plot.
      ctx.fillStyle = 'rgba(150,162,178,0.55)';
      ctx.textBaseline = 'top';
      ctx.font = '8px ui-sans-serif, system-ui, -apple-system, sans-serif';
      const zone = (label, f0, f1) => ctx.fillText(label, fx((f0 + f1) / 2), py + 2);
      zone('SHADOWS', 0, 0.33);
      zone('MIDTONES', 0.33, 0.66);
      zone('HIGHLIGHTS', 0.66, 1);

      // Channel fills, clipped to the plot rect.
      let max = 1;
      for (let i = 0; i < bins; i += 1) max = Math.max(max, r[i], g[i], b[i], l[i]);
      ctx.save();
      ctx.beginPath();
      ctx.rect(px, py, pw, ph);
      ctx.clip();
      const drawChan = (arr, color, comp) => {
        ctx.globalCompositeOperation = comp;
        ctx.beginPath();
        ctx.moveTo(px, py + ph);
        for (let i = 0; i < bins; i += 1) ctx.lineTo(px + (i / (bins - 1)) * pw, py + ph - (arr[i] / max) * ph);
        ctx.lineTo(px + pw, py + ph);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
      };
      drawChan(l, 'rgba(150,160,175,0.45)', 'source-over');
      drawChan(r, 'rgba(255,70,70,0.7)', 'screen');
      drawChan(g, 'rgba(70,230,90,0.7)', 'screen');
      drawChan(b, 'rgba(70,120,255,0.7)', 'screen');
      ctx.globalCompositeOperation = 'source-over';
      ctx.restore();

      // Plot frame.
      ctx.strokeStyle = 'rgba(255,255,255,0.14)';
      ctx.lineWidth = 1;
      ctx.strokeRect(px + 0.5, py + 0.5, pw - 1, ph - 1);
      return;
    }

    // ImageData-based scopes draw straight to device pixels (putImageData
    // ignores the transform), so use the full backing-store dimensions.
    const W = canvas.width;
    const H = canvas.height;

    if ((active === 'wave' || active === 'parade') && stats.wfL) {
      const { cols, rows } = stats;
      const img = ctx.createImageData(W, H);
      const d = img.data;
      const put = (wf, panX, panW, cr, cg, cb) => {
        for (let cx = 0; cx < W; cx += 1) {
          if (cx < panX || cx >= panX + panW) continue;
          const col = (((cx - panX) / panW) * cols) | 0;
          let cmax = 1;
          for (let ry = 0; ry < rows; ry += 1) cmax = Math.max(cmax, wf[col * rows + ry]);
          for (let y = 0; y < H; y += 1) {
            const ry = ((y / H) * rows) | 0;
            const v = wf[col * rows + ry] / cmax;
            if (v <= 0) continue;
            const a = Math.min(1, v * 1.6);
            const o = (y * W + cx) * 4;
            d[o] = Math.min(255, d[o] + cr * a);
            d[o + 1] = Math.min(255, d[o + 1] + cg * a);
            d[o + 2] = Math.min(255, d[o + 2] + cb * a);
            d[o + 3] = 255;
          }
        }
      };
      if (active === 'wave') {
        put(stats.wfL, 0, W, 180, 230, 190);
      } else {
        const third = Math.floor(W / 3);
        put(stats.wfR, 0, third, 255, 60, 60);
        put(stats.wfG, third, third, 60, 220, 90);
        put(stats.wfB, third * 2, W - third * 2, 70, 120, 255);
      }
      ctx.putImageData(img, 0, 0);
      // IRE guide lines + labels (0 / 25 / 50 / 75 / 100).
      ctx.font = `${Math.round(9 * dpr)}px ui-sans-serif, system-ui, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      [0, 0.25, 0.5, 0.75, 1].forEach((f) => {
        const y = H - f * H;
        ctx.strokeStyle = 'rgba(255,255,255,0.09)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, Math.min(H - 1, Math.max(1, y)));
        ctx.lineTo(W, Math.min(H - 1, Math.max(1, y)));
        ctx.stroke();
        ctx.fillStyle = 'rgba(180,190,205,0.6)';
        ctx.fillText(`${Math.round(f * 100)}`, 3 * dpr, Math.min(H - 7 * dpr, Math.max(7 * dpr, y)));
      });
      return;
    }

    if (active === 'vector' && stats.vec) {
      const { vec, vec_n: n } = stats;
      let max = 1;
      for (let i = 0; i < vec.length; i += 1) max = Math.max(max, vec[i]);
      const img = ctx.createImageData(W, H);
      const d = img.data;
      for (let y = 0; y < H; y += 1) {
        const vy = ((y / H) * n) | 0;
        for (let x = 0; x < W; x += 1) {
          const vx = ((x / W) * n) | 0;
          const v = vec[vy * n + vx] / max;
          if (v <= 0) continue;
          const a = Math.min(255, Math.sqrt(v) * 320);
          const o = (y * W + x) * 4;
          d[o] = a;
          d[o + 1] = Math.min(255, a * 0.9 + 40);
          d[o + 2] = Math.min(255, a * 0.6);
          d[o + 3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);
      const cxp = W / 2;
      const cyp = H / 2;
      const rad = Math.min(W, H) * 0.46;
      // Graticule rings (75% + 100% saturation) and crosshair.
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cxp, cyp, rad, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.beginPath();
      ctx.arc(cxp, cyp, rad * 0.75, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cxp, cyp - rad);
      ctx.lineTo(cxp, cyp + rad);
      ctx.moveTo(cxp - rad, cyp);
      ctx.lineTo(cxp + rad, cyp);
      ctx.stroke();
      // Skin-tone (I) line at ~123° (-90-57+... legacy angle preserved).
      ctx.beginPath();
      ctx.moveTo(cxp, cyp);
      ctx.lineTo(
        cxp + Math.cos(((-90 - 57) * Math.PI) / 180) * rad,
        cyp + Math.sin(((-90 - 57) * Math.PI) / 180) * rad,
      );
      ctx.strokeStyle = 'rgba(255,180,140,0.55)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }, [stats, active]);

  return (
    <div className="cs-scopes-wrap">
      {!forcedMode && (
        <div className="cs-segment cs-segment-sm cs-scopes-modes">
          {MODES.map((m) => (
            <button key={m.id} className={active === m.id ? 'on' : ''} onClick={() => setMode(m.id)}>{m.label}</button>
          ))}
        </div>
      )}
      <canvas ref={canvasRef} width={300} height={172} className="cs-scopes" />
      {active === 'hist' && (
        <div className="cs-scopes-legend">
          <span className="cs-scopes-leg"><i style={{ background: 'rgba(255,70,70,0.9)' }} />R</span>
          <span className="cs-scopes-leg"><i style={{ background: 'rgba(70,230,90,0.9)' }} />G</span>
          <span className="cs-scopes-leg"><i style={{ background: 'rgba(70,120,255,0.9)' }} />B</span>
          <span className="cs-scopes-leg"><i style={{ background: 'rgba(150,160,175,0.9)' }} />Luma</span>
          <span className="cs-scopes-legnote">x: tonal value % (0 blk → 100 wht) · dashed 18% grey · red = clipping</span>
        </div>
      )}
    </div>
  );
}
